import {
  DOCUMENT_DISABILITY_STATEMENT_PROVIDER_SEARCH,
  DOCUMENT_EASY_LANGUAGE,
  DOCUMENT_PRIVACY_POLICY,
  DOCUMENT_SIGN_LANGUAGE,
  DOCUMENT_TERMS_AND_CONDITIONS,
  FEDERAL_STATES,
  LINKS_RECARE,
  PROVIDER_SEARCH_BAVARIA,
  PROVIDER_SEARCH_DASHBOARD_URLS,
  PROVIDER_SEARCH_WESER_EMS,
  WESER_EMS_FILTERED_ZIPCODES,
  WESER_EMS_IMPRESSUM_URL,
} from "core/consts";
import { pageMetaDataBavaria } from "core/model/providerSearch/pageMeta/bavaria";
import { pageMetaDataWeserEms } from "core/model/providerSearch/pageMeta/weserems";
import {
  ActiveProviderSearch,
  IconType,
  LegalDocumentType,
  ProviderSearchPage,
  ValueOf,
} from "core/types";
import { ExternalLinkType } from "ds_legacy/components/LinkV2";
import { MailIcon } from "lucide-react";
import Translations from "translations/types";

export type ProviderSearchConfig = {
  allowedFederalState: ValueOf<typeof FEDERAL_STATES>;
  printTitle: (translations: Translations) => string;
  filteredZipcodes: string[];
  logos: {
    default: { altText: (translations: Translations) => string; path: string };
    favicon: string;
    mobile: string;
    keyVisual: string;
  };
  pageMetaData: (page: ProviderSearchPage) => {
    title: (translations: Translations) => string;
    description?: (translations: Translations) => string;
  };
  welcomeScreen: {
    title: (translations: Translations) => string;
    description: (translations: Translations) => string;
  };
  footer: {
    accessibilityCertificate?: {
      logo: string;
      url: string;
    };
    sponsors: {
      className: string;
      logo: string;
      url?: string;
      altText: (translations: Translations) => string;
    };
    participants: {
      items: {
        label: (translations: Translations) => string;
        href?: string;
        id: string;
      }[];
    };
    links_and_contact_info: {
      items: {
        ariaLabel?: {
          label: (translations: Translations) => string;
          type?: ExternalLinkType;
        };
        label: (translations: Translations) => string;
        href?: (translations: Translations) => string;
        legalDocumentType?: LegalDocumentType;
        icon?: IconType;
        type?: ExternalLinkType;
        id: string;
      }[];
    };
  };
  mapPlaceholderUrl: string;
  nameString: (translations: Translations) => string;
  emailAddress: string;
  phoneNumber: string;
};

export const PROVIDER_SEARCH_EMAIL_ADDRESS: Record<
  ActiveProviderSearch,
  string
> = {
  [PROVIDER_SEARCH_BAVARIA]: "pflege-finder-bayern@recaresolutions.com",
  [PROVIDER_SEARCH_WESER_EMS]: "pflegeportal-weser-ems@recaresolutions.com",
};

export const PROVIDER_SEARCH_PHONE_NUMBER: Record<
  ActiveProviderSearch,
  string
> = {
  [PROVIDER_SEARCH_BAVARIA]: "+49 30 31196372",
  [PROVIDER_SEARCH_WESER_EMS]: "+49 30 91732114",
};

export const PROVIDER_SEARCH_CONFIG: Record<
  ActiveProviderSearch,
  ProviderSearchConfig
> = {
  [PROVIDER_SEARCH_BAVARIA]: {
    allowedFederalState: FEDERAL_STATES.BAYERN,
    emailAddress: PROVIDER_SEARCH_EMAIL_ADDRESS[PROVIDER_SEARCH_BAVARIA],
    filteredZipcodes: [],
    footer: {
      accessibilityCertificate: {
        logo: "https://bitvtest.de/fileadmin/user_upload/bik-bitv-konform-h44px.svg",
        url: "https://report.bitvtest.de/ba8a7cd7-4ebb-4b40-9374-e6fd7b698ba1.html",
      },
      links_and_contact_info: {
        items: [
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.impressum,
            },
            href: () => LINKS_RECARE.imprint,
            id: "impressum",
            label: (translations: Translations) =>
              translations.providersearch.footer.impressum,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.privacyPolicy,
              type: "pdf",
            },
            id: "privacy-policy",
            label: (translations: Translations) =>
              translations.providersearch.footer.privacyPolicy,
            legalDocumentType: DOCUMENT_PRIVACY_POLICY,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.termsOfUse,
              type: "pdf",
            },
            id: "terms-of-use",
            label: (translations: Translations) =>
              translations.providersearch.footer.termsOfUse,
            legalDocumentType: DOCUMENT_TERMS_AND_CONDITIONS,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.about,
            },
            href: () => PROVIDER_SEARCH_DASHBOARD_URLS[PROVIDER_SEARCH_BAVARIA],
            id: "about",
            label: (translations: Translations) =>
              translations.providersearch.footer.about,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.accessibilityDeclaration,
              type: "pdf",
            },
            id: "accessibility-declaration",
            label: (translations: Translations) =>
              translations.providersearch.footer.accessibilityDeclaration,
            legalDocumentType: DOCUMENT_DISABILITY_STATEMENT_PROVIDER_SEARCH,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.easyLanguage,
              type: "pdf",
            },
            id: "easy-language",
            label: (translations: Translations) =>
              translations.providersearch.footer.easyLanguage,
            legalDocumentType: DOCUMENT_EASY_LANGUAGE,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.easyLanguage,
              type: "pdf",
            },
            id: "sign-language",
            label: (translations: Translations) =>
              translations.providersearch.footer.signLanguage,
            legalDocumentType: DOCUMENT_SIGN_LANGUAGE,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.reportBarrier,
              type: "email",
            },
            href: (translations: Translations) =>
              translations.providersearch.footer.reportBarrierLink({
                email: PROVIDER_SEARCH_EMAIL_ADDRESS[PROVIDER_SEARCH_BAVARIA],
              }),
            id: "report-barrier",
            label: (translations: Translations) =>
              translations.providersearch.footer.reportBarrier,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.reportContent,
              type: "email",
            },
            href: (translations: Translations) =>
              translations.providersearch.footer.reportContentLink({
                email: PROVIDER_SEARCH_EMAIL_ADDRESS[PROVIDER_SEARCH_BAVARIA],
              }),
            icon: MailIcon,
            id: "report-content",
            label: (translations: Translations) =>
              translations.providersearch.footer.reportContent,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.phone({
                  phone: PROVIDER_SEARCH_PHONE_NUMBER[PROVIDER_SEARCH_BAVARIA],
                }),
              type: "phone",
            },
            href: () =>
              `tel:${PROVIDER_SEARCH_PHONE_NUMBER[
                PROVIDER_SEARCH_BAVARIA
              ].replaceAll(" ", "")}`,
            id: "phone",
            label: (translations: Translations) =>
              translations.providersearch.footer.phoneNumberLabel({
                phone: PROVIDER_SEARCH_PHONE_NUMBER[PROVIDER_SEARCH_BAVARIA],
              }),
            type: "phone",
          },
          {
            id: "email-address",
            label: (translations: Translations) =>
              translations.providersearch.footer.emailAddress({
                email: PROVIDER_SEARCH_EMAIL_ADDRESS[PROVIDER_SEARCH_BAVARIA],
              }),
          },
        ],
      },
      participants: {
        items: [
          {
            id: "partners-insurance",
            label: (translations: Translations) =>
              translations.providersearch.footer.partnersInsurance,
          },
          {
            href: "https://www.bkg-online.de/",
            id: "partners-hospitals",
            label: (translations: Translations) =>
              translations.providersearch.footer.partnersHospitals,
          },
          {
            href: "https://www.lfp.bayern.de/",
            id: "partners-lfp",
            label: (translations: Translations) =>
              translations.providersearch.footer.partnersLfp,
          },
        ],
      },
      sponsors: {
        altText: (translations: Translations) =>
          translations.providersearch.footer.altTextLogoStaatsministerium,
        className: "h-[60px] desktop:h-[72px]",
        logo: "bcp-stmgp-logo.svg",
        url: PROVIDER_SEARCH_DASHBOARD_URLS[PROVIDER_SEARCH_BAVARIA],
      },
    },
    logos: {
      default: {
        altText: (translations: Translations) =>
          translations.providersearch.listingPage.accessibility.altTextLogo,
        path: "Pflegefinder_Bayern_Logo_Header.png",
      },
      favicon: "Pflegefinder_Bayern_Favicon.ico",
      keyVisual: "Pflegefinder_Bayern_Key_Visual.jpg",
      mobile: "Pflegefinder_Bayern_Logo_Header.png",
    },
    mapPlaceholderUrl: "bcp-map-placeholder-blur.png",
    nameString: (translations: Translations) =>
      translations.providersearch.nameBavaria,
    pageMetaData: pageMetaDataBavaria,
    phoneNumber: PROVIDER_SEARCH_PHONE_NUMBER[PROVIDER_SEARCH_BAVARIA],
    printTitle: (translations: Translations) =>
      translations.providersearch.listingPage.printTitle,
    welcomeScreen: {
      description: (translations: Translations) =>
        translations.providersearch.startPage.paragraphOne,
      title: (translations: Translations) =>
        translations.providersearch.startPage.title,
    },
  },
  [PROVIDER_SEARCH_WESER_EMS]: {
    allowedFederalState: FEDERAL_STATES.NIEDERSACHSEN,
    emailAddress: PROVIDER_SEARCH_EMAIL_ADDRESS[PROVIDER_SEARCH_WESER_EMS],
    filteredZipcodes: WESER_EMS_FILTERED_ZIPCODES,
    footer: {
      links_and_contact_info: {
        items: [
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.impressum,
            },
            href: () => WESER_EMS_IMPRESSUM_URL,
            id: "impressum",
            label: (translations: Translations) =>
              translations.providersearch.footer.impressum,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.privacyPolicy,
              type: "pdf",
            },
            id: "privacy-policy",
            label: (translations: Translations) =>
              translations.providersearch.footer.privacyPolicy,
            legalDocumentType: DOCUMENT_PRIVACY_POLICY,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.termsOfUse,
              type: "pdf",
            },
            id: "terms-of-use",
            label: (translations: Translations) =>
              translations.providersearch.footer.termsOfUse,
            legalDocumentType: DOCUMENT_TERMS_AND_CONDITIONS,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.accessibilityDeclaration,
              type: "pdf",
            },
            id: "accessibility-declaration",
            label: (translations: Translations) =>
              translations.providersearch.footer.accessibilityDeclaration,
            legalDocumentType: DOCUMENT_DISABILITY_STATEMENT_PROVIDER_SEARCH,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.easyLanguage,
              type: "pdf",
            },
            id: "easy-language",
            label: (translations: Translations) =>
              translations.providersearch.footer.easyLanguage,
            legalDocumentType: DOCUMENT_EASY_LANGUAGE,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.easyLanguage,
              type: "pdf",
            },
            id: "sign-language",
            label: (translations: Translations) =>
              translations.providersearch.footer.signLanguage,
            legalDocumentType: DOCUMENT_SIGN_LANGUAGE,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.reportBarrier,
              type: "email",
            },
            href: (translations: Translations) =>
              translations.providersearch.footer.reportBarrierLink({
                email: PROVIDER_SEARCH_EMAIL_ADDRESS[PROVIDER_SEARCH_WESER_EMS],
              }),
            id: "report-barrier",
            label: (translations: Translations) =>
              translations.providersearch.footer.reportBarrier,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.reportContent,
              type: "email",
            },
            href: (translations: Translations) =>
              translations.providersearch.footer.reportContentLink({
                email: PROVIDER_SEARCH_EMAIL_ADDRESS[PROVIDER_SEARCH_WESER_EMS],
              }),
            icon: MailIcon,
            id: "report-content",
            label: (translations: Translations) =>
              translations.providersearch.footer.reportContent,
          },
          {
            ariaLabel: {
              label: (translations: Translations) =>
                translations.providersearch.footer.phone({
                  phone:
                    PROVIDER_SEARCH_PHONE_NUMBER[PROVIDER_SEARCH_WESER_EMS],
                }),
              type: "phone",
            },
            href: () =>
              `tel:${PROVIDER_SEARCH_PHONE_NUMBER[
                PROVIDER_SEARCH_WESER_EMS
              ].replaceAll(" ", "")}`,
            id: "phone",
            label: (translations: Translations) =>
              translations.providersearch.footer.phoneNumberLabel({
                phone: PROVIDER_SEARCH_PHONE_NUMBER[PROVIDER_SEARCH_WESER_EMS],
              }),
            type: "phone",
          },
          {
            id: "email-address",
            label: (translations: Translations) =>
              translations.providersearch.footer.emailAddress({
                email: PROVIDER_SEARCH_EMAIL_ADDRESS[PROVIDER_SEARCH_WESER_EMS],
              }),
          },
        ],
      },
      participants: {
        items: [
          {
            id: "ammerland",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.ammerland,
          },
          {
            id: "aurich",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.aurich,
          },
          {
            id: "cloppenburg",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.cloppenburg,
          },
          {
            id: "delmenhorst",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.delmenhorst,
          },
          {
            id: "emsland",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.emsland,
          },
          {
            id: "friesland",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.friesland,
          },
          {
            id: "leer",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.leer,
          },
          {
            id: "oldenburg",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.oldenburg,
          },
          {
            id: "stadt-oldenburg",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.stadtOldenburg,
          },
          {
            id: "osnabrueck",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.osnabrueck,
          },
          {
            id: "stadt-osnabrueck",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.stadtOsnabrueck,
          },
          {
            id: "vechta",
            label: (translations: Translations) =>
              translations.providersearch.footerWeserEms.vechta,
          },
        ],
      },
      sponsors: {
        altText: (translations: Translations) =>
          translations.providersearch.footerWeserEms.altTextLogoMinistry,
        className: "h-[90px] desktop:h-[102px]",
        logo: "logo/png/weserems-sponsor.png",
      },
    },
    logos: {
      default: {
        altText: (translations: Translations) =>
          translations.providersearchWeserEms.altTextWeserEmsLogo,
        path: "logo/png/weserems-logo-white-bg.png",
      },
      favicon: "logo/ico/weserems-favicon-white-bg.ico",
      keyVisual: "Weser_Ems_Key_Visual.png",
      mobile: "logo/png/weserems-logo-white-bg.png",
    },
    mapPlaceholderUrl: "Weser_Ems_Map_Blur.png",
    nameString: (translations: Translations) =>
      translations.providersearch.nameWeserEms,
    pageMetaData: pageMetaDataWeserEms,
    phoneNumber: PROVIDER_SEARCH_PHONE_NUMBER[PROVIDER_SEARCH_WESER_EMS],
    printTitle: (translations: Translations) =>
      translations.providersearchWeserEms.titlePrintPage,
    welcomeScreen: {
      description: (translations: Translations) =>
        translations.providersearch.startPage.paragraphOneWeserEms,
      title: (translations: Translations) =>
        translations.providersearch.startPage.titleWeserEms,
    },
  },
};
